import type { AggregateGroup, AggregateDef } from '@/features/sliced-form/schemas/aggregate-entity'

/** Fixed number of stat-card slots across the top of a stats panel. */
export const STAT_CARD_SLOT_COUNT = 4

/** Persisted slot picks: one aggregate id (or null) per slot, in slot order. */
export type StatCardSlots = (string | null)[]

/**
 * Panels that offer the stat-card row, with each one's DEFAULT visibility
 * (what an unset `showCards` means). Market Stats ships with the row on; Ticker
 * Stats keeps it opt-in since its tabs already sit above the content. The panel
 * header's more-menu keys its "Show cards" toggle on membership here.
 */
export const STAT_CARDS_DEFAULT_VISIBLE: Readonly<Record<string, boolean>> = {
  'ticker-stats': false,
  'history-stats': true,
}

export const STAT_CARD_COMPONENT_TYPES: ReadonlySet<string> = new Set(
  Object.keys(STAT_CARDS_DEFAULT_VISIBLE),
)

/** Resolve the persisted flag (or its absence) to the row's visibility for a panel type. */
export function isStatCardsVisible(componentType: string, showCards: boolean | undefined): boolean {
  return showCards ?? STAT_CARDS_DEFAULT_VISIBLE[componentType] ?? false
}

/**
 * Title colour per slot POSITION (not per metric): a fixed four-way rotation so
 * the row reads as four distinct cards even when every metric is an average.
 * Light/dark pairs follow the aggregate chip badge convention.
 */
export const STAT_CARD_TITLE_CLASSES: readonly string[] = [
  'text-indigo-600 dark:text-indigo-400',
  'text-emerald-600 dark:text-emerald-400',
  'text-cyan-600 dark:text-cyan-400',
  'text-amber-600 dark:text-amber-400',
]

/**
 * Every aggregate id the panel renders, in table order (group by group), with
 * ids that have no definition dropped and duplicates collapsed. This is the
 * option list for each card's picker and the source of positional defaults.
 */
export function orderedAggregateIds(
  groups: AggregateGroup[],
  defs: Record<string, AggregateDef>,
): string[] {
  const seen = new Set<string>()
  const ids: string[] = []
  for (const group of groups) {
    for (const id of group.data) {
      if (!defs[id] || seen.has(id)) continue
      seen.add(id)
      ids.push(id)
    }
  }
  return ids
}

/**
 * Resolve the persisted picks against the CURRENT profile. A saved id that is
 * still in the profile wins; anything else (never picked, or the metric was
 * removed) falls back to the metric at that slot's position, or null when the
 * profile has fewer metrics than slots. Always returns exactly
 * STAT_CARD_SLOT_COUNT entries.
 */
export function resolveStatCardSlots(
  saved: StatCardSlots | undefined,
  orderedIds: string[],
): StatCardSlots {
  const valid = new Set(orderedIds)
  return Array.from({ length: STAT_CARD_SLOT_COUNT }, (_, i) => {
    const pick = saved?.[i]
    if (pick && valid.has(pick)) return pick
    return orderedIds[i] ?? null
  })
}

/**
 * Aggregate ids come from a resetting counter (`agg_1`, `agg_2`, ...), so two
 * saved profiles can reuse the same id for different metrics. When the applied
 * profile CHANGES identity, the persisted picks must be dropped or a card would
 * silently show a different stat. Returns the patch to spread into the
 * updatePanel call: clears the slots on a swap, nothing on a same-profile Apply.
 */
export function statCardSlotsPatchForProfile(
  previousProfileId: string | null | undefined,
  nextProfileId: string | null,
): { cardSlots?: undefined } {
  return (previousProfileId ?? null) === nextProfileId ? {} : { cardSlots: undefined }
}
